// Formatação compartilhada do painel de negócio (#196). Datas SEMPRE em
// America/Fortaleza — o backend grava UTC (timestamptz) e o operador pode
// estar em qualquer fuso; sem pinar o timeZone o painel mostraria +3h errado
// (bug histórico do emulador Android, ver CLAUDE.md "Problemas Conhecidos").

import { sportFullLabel } from './sportCatalog';

const TZ = 'America/Fortaleza';

const dateTimeFmt = new Intl.DateTimeFormat('pt-BR', {
  timeZone: TZ,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const timeFmt = new Intl.DateTimeFormat('pt-BR', {
  timeZone: TZ,
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

const weekStartFmt = new Intl.DateTimeFormat('pt-BR', {
  timeZone: TZ,
  day: 'numeric',
  month: 'short',
});

/// Barra DIÁRIA (#943): "05/08". Curto porque 91 barras de 90 dias não cabem
/// com "5 de ago." no eixo.
const dayStartFmt = new Intl.DateTimeFormat('pt-BR', {
  timeZone: TZ,
  day: '2-digit',
  month: '2-digit',
});

const monthStartFmt = new Intl.DateTimeFormat('pt-BR', {
  timeZone: TZ,
  month: 'short',
  year: 'numeric',
});

const percentFmt = new Intl.NumberFormat('pt-BR', {
  style: 'percent',
  maximumFractionDigits: 0,
});

/// UTC ISO → "04/07/2026 09:32" no fuso de Fortaleza. Null/inválido → "-".
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return dateTimeFmt.format(d);
}

/// UTC ISO → "09:32:07" (Fortaleza). Usado no scrubber/timeline do replay.
export function formatClock(iso: string | null | undefined): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return timeFmt.format(d);
}

/// Início de barra dos gráficos do painel (#525/#541 e #943) → "05/08"
/// (diária), "3 de ago." (semanal) ou "ago. 2026" (mensal). Mesmo fuso do resto
/// do painel: o backend bucketiza em horário de Brasília (`BrazilTime`) e emite
/// o instante UTC da meia-noite local, então formatar em UTC-3 devolve
/// exatamente o dia / a segunda-feira / o dia 1 que o servidor agrupou.
export function formatBucketStart(
  iso: string | null | undefined,
  granularity: 'day' | 'week' | 'month' = 'week',
): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  if (granularity === 'day') return dayStartFmt.format(d);
  return (granularity === 'month' ? monthStartFmt : weekStartFmt).format(d);
}

/// Ratio (0–1) → "49%". Sem casa decimal: a precisão que o painel promete.
export function formatPercent(ratio: number): string {
  return percentFmt.format(ratio);
}

/// Duração entre início e fim. Sessão em andamento (endTime null) → "ao vivo"
/// em vez de NaN. Sem início → "-".
export function formatDuration(
  startIso: string | null | undefined,
  endIso: string | null | undefined,
): string {
  if (!startIso) return '-';
  if (!endIso) return 'ao vivo';
  const start = new Date(startIso).getTime();
  const end = new Date(endIso).getTime();
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return '-';
  return formatDurationMs(end - start);
}

export function formatDurationMs(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '-';
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  const s = Math.floor((ms % 60000) / 1000);
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
}

/// metros → "12,4 km". Distâncias curtas (<1 km) em metros.
export function formatDistance(meters: number): string {
  if (!Number.isFinite(meters) || meters <= 0) return '0 km';
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} km`;
}

/// Rótulo do atleta: nome do perfil quando existe, senão UID abreviado
/// (`abc12345…`) — nunca o UID cru inteiro na tabela. Retorna também se é
/// fallback, pra UI poder estilizar (mono/dim) o UID.
export function athleteLabel(
  displayName: string | null | undefined,
  userId: string | null | undefined,
): { label: string; isFallback: boolean } {
  const name = displayName?.trim();
  if (name) return { label: name, isFallback: false };
  const uid = (userId ?? '').trim();
  if (!uid) return { label: '-', isFallback: true };
  return { label: uid.length > 10 ? `${uid.slice(0, 8)}…` : uid, isFallback: true };
}

/// Sessões de Evento — o campo `category` do banco NÃO é modalidade nelas.
const EVENT_SESSION_TYPES = new Set(['race', 'support']);

/// Modalidade esportiva da sessão (kitesurf, foil, wing…).
/// - Freeride/DW: o campo `category` do banco JÁ é a modalidade → mostra.
/// - Evento (`race`/`support`): `category` guarda a CATEGORIA da prova (divisão:
///   Geral/PRO/Feminino), não a modalidade → fica em branco pra não misturar os
///   dois conceitos.
/// DÉBITO TÉCNICO: evento não carrega modalidade própria (nem schema nem mobile).
/// Quando carregar, esta função passa a lê-la em vez de retornar branco.
export function modalityLabel(
  sessionType: string,
  category: string | null | undefined,
): string {
  if (EVENT_SESSION_TYPES.has(sessionType)) return '-';
  const raw = category?.trim();
  if (!raw) return '-';
  // Apoio em terra do DW carimba a categoria reservada 'support' (fora do
  // placar por construção, decisão 28/07) — no painel vira "Apoio".
  if (raw === 'support') return 'Apoio';
  // #433 — slug do catálogo vira rótulo desambiguado ("Remo · SUP Foil");
  // valor legado fora do catálogo segue cru (não inventa).
  return sportFullLabel(raw);
}

const KNOTS_TO_KMH = 1.852;

/// nós → "33,9 km/h". O painel exibe em km/h (pedido do Felipe), mas a fonte
/// gravada é nós (MaxSpeedKnots, Doppler do chip — Invariante #2); a conversão
/// é só de apresentação, não recalcular de posição aqui.
export function formatSpeedKmh(knots: number): string {
  if (!Number.isFinite(knots) || knots <= 0) return '-';
  const kmh = knots * KNOTS_TO_KMH;
  return `${kmh.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} km/h`;
}
